/**
 * Stock helpers shared across the cart UI and actions.
 *
 * A cart line's `stock` is the effective number of units available for that
 * product/variant: a finite number when inventory is tracked, or `null`/
 * `undefined` when untracked (unlimited). This mirrors the server-side
 * `preflightStockCheck` semantics (untracked or null stock ⇒ no limit).
 */

/** Units available to add/select for a line; Infinity when untracked/unlimited. */
export function availableUnits(stock: number | null | undefined): number {
  return stock === null || stock === undefined ? Infinity : Math.max(0, stock);
}

/**
 * Effective available stock for a line, from the raw product/variant fields.
 * - Variant lines: the variant's own stock (null ⇒ untracked).
 * - Simple lines: the product stock only when `track_stock` is on.
 */
export function effectiveStock(input: {
  isVariant: boolean;
  variantStock?: number | null;
  trackStock?: boolean | null;
  productStock?: number | null;
}): number | null {
  if (input.isVariant) return input.variantStock ?? null;
  return input.trackStock && input.productStock != null ? input.productStock : null;
}

/**
 * The quantity a cart line should hold, given the product's own rules.
 *
 * `minOrderQty` is a property of the product, so the server resolves it rather
 * than accepting it from the caller: a guest cart that predates the merchant
 * raising the minimum — or a hand-edited one — would otherwise land a line
 * below the quantity the product is actually sold in.
 *
 * Order matters. The minimum is applied first and the stock ceiling second, so
 * a product whose remaining stock is below its own minimum clamps to the stock
 * rather than inventing units that do not exist.
 */
export function resolveLineQuantity(input: {
  /** Units already on the line (0 when it is being created). */
  existingQty: number;
  /** Units being added. */
  addQty: number;
  /** The product's minimum order quantity; anything falsy means 1. */
  minOrderQty?: number | null;
  /** Effective stock: null/undefined ⇒ untracked ⇒ unlimited. */
  stock: number | null | undefined;
}): number {
  const floor = Math.max(1, Math.floor(input.minOrderQty || 1));
  const wanted = input.existingQty + input.addQty;
  return Math.min(Math.max(wanted, floor), availableUnits(input.stock));
}

/** Thrown by the cart when an add would exceed the available inventory. */
export class CartStockError extends Error {
  readonly available: number;
  constructor(available: number) {
    super('cart_stock_exceeded');
    this.name = 'CartStockError';
    this.available = available;
  }
}
